"use client";

import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

interface MonthChartItem {
  label: string;
  value: number;
}

interface Props {
  chartData: MonthChartItem[];
}

const MonthlyOrderChart: React.FC<Props> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        panY: false,
        panX: false,
        wheelY: "zoomX"
      })
    );

    // X Axis (show all labels)
	const xAxis = chart.xAxes.push(
	  am5xy.CategoryAxis.new(root, {
		categoryField: "label",
		renderer: am5xy.AxisRendererX.new(root, {
		  minGridDistance: 10, // <- show all labels closer together
		  cellStartLocation: 0.1,
		  cellEndLocation: 0.9
		})
	  })
	);

	// Force labels to always display
	xAxis.get("renderer").labels.template.setAll({
	  oversizedBehavior: "wrap",  // "none" = show full even if overlaps
	  maxWidth: 60,               // optional width for wrapping
	});

    // Y Axis (values)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    );

    // Bar series
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Orders",
        xAxis,
        yAxis,
        valueYField: "value",
        categoryXField: "label"
      })
    );

    // Style bars
    series.columns.template.setAll({
      tooltipText: "{categoryX}: {valueY}",
      strokeOpacity: 0,
      cornerRadiusTL: 5,
      cornerRadiusTR: 5
    });

    // Set data
    xAxis.data.setAll(chartData);
    series.data.setAll(chartData);

    // Add cursor
    chart.set("cursor", am5xy.XYCursor.new(root, {}));

    return () => {
      root.dispose(); // cleanup on unmount
    };
  }, [chartData]);

  return (
    <div
      ref={chartRef}
      style={{ width: "100%", height: "100%" }}
    ></div>
  );
};

export default MonthlyOrderChart;
